import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useReducedMotion } from "../hooks/useReducedMotion.js";
import { formatRating } from "../lib/format.js";
import type { ReviewSummary } from "../lib/types.js";

interface MarqueeProps {
  items: ReviewSummary[];
  /** Seconds for one full pass of the list. Longer reads calmer. */
  secondsPerItem?: number;
}

/**
 * One entry per film.
 *
 * The ticker is fed `kind: "all"`, and a film Davis quick-rated and later wrote
 * up properly exists as two rows — so the same title appeared twice in the
 * strip, once with each score. The written review wins because it is the one
 * with somewhere to link to.
 */
function oneEntryPerFilm(items: ReviewSummary[]): ReviewSummary[] {
  const byFilm = new Map<string, ReviewSummary>();

  for (const item of items) {
    const key = `${item.filmTitle.trim().toLowerCase()}|${item.filmYear ?? ""}`;
    const held = byFilm.get(key);

    if (!held || (held.kind === "quick" && item.kind === "full")) {
      byFilm.set(key, item);
    }
  }

  return [...byFilm.values()];
}

/**
 * Ticker of recent films and scores running under the hero.
 *
 * ## Why the copy count is measured rather than fixed
 *
 * The track holds N identical copies of the list and the CSS translates it by
 * `-100% / N`, which lands copy two exactly where copy one began. That is
 * seamless *only while the track is wider than the viewport*. With two copies
 * and a short catalogue — a new site, or a narrow list on a wide monitor — the
 * strip ran out mid-screen and the loop restarted in plain sight, the same
 * titles visibly snapping back to the left. So the copy count is derived from
 * the measured width of one copy against the measured width of the container:
 * enough copies to cover the viewport twice over, whatever the list length.
 *
 * The duration scales with the number of entries too. A fixed 42 seconds meant
 * eight films crawled and fifty raced.
 *
 * With reduced motion the animation class is dropped and the strip becomes a
 * plain horizontally scrollable row, so the same information is still reachable
 * without anything moving on its own.
 */
export function Marquee({ items, secondsPerItem = 3.5 }: MarqueeProps) {
  const reduced = useReducedMotion();
  const frame = useRef<HTMLDivElement>(null);
  const copy = useRef<HTMLUListElement>(null);
  const [copies, setCopies] = useState(2);

  const films = useMemo(() => oneEntryPerFilm(items), [items]);

  useEffect(() => {
    if (reduced) return;

    const measure = () => {
      const listWidth = copy.current?.scrollWidth ?? 0;
      const frameWidth = frame.current?.clientWidth ?? 0;
      if (listWidth === 0 || frameWidth === 0) return;

      // Two viewports' worth: one on screen, one queued to slide in behind it.
      // The minimum of 2 is what makes the -100%/N translate a seamless loop at
      // all — a single copy has nothing to hand over to.
      setCopies(Math.max(2, Math.ceil((frameWidth * 2) / listWidth)));
    };

    measure();

    // Fonts and images settle after first paint, and the container width
    // changes on rotate and resize; both move the measurement.
    const observer = new ResizeObserver(measure);
    if (frame.current) observer.observe(frame.current);
    if (copy.current) observer.observe(copy.current);

    return () => observer.disconnect();
  }, [reduced, films]);

  if (films.length === 0) return null;

  const duration = Math.max(24, films.length * secondsPerItem);

  return (
    <div
      ref={frame}
      // Opaque, not `/70`. The reel sits on a layer behind the page, and a
      // translucent band let it show straight through the titles — the wheel
      // appeared to be sliding along inside the ticker.
      className={`border-y border-ink-700 bg-ink-950 py-3.5 ${
        reduced ? "overflow-x-auto" : "overflow-hidden"
      }`}
    >
      <div
        className={`flex w-max ${reduced ? "" : "marquee-track"}`}
        style={
          {
            "--marquee-duration": `${duration}s`,
            "--marquee-shift": `${-100 / (reduced ? 1 : copies)}%`,
          } as React.CSSProperties
        }
      >
        {Array.from({ length: reduced ? 1 : copies }, (_, index) => (
          <ul
            key={index}
            // A screen reader should hear the list once, not once per copy.
            aria-hidden={index > 0}
            ref={index === 0 ? copy : undefined}
            className="flex shrink-0 items-center"
          >
            {films.map((item) => (
              <li key={`${index}-${item.id}`} className="flex items-center">
                <Link
                  to={item.kind === "quick" ? "/ratings" : `/reviews/${item.slug}`}
                  // Copies after the first are decorative duplicates; leaving
                  // them focusable would make Tab walk the same list N times.
                  tabIndex={index > 0 ? -1 : undefined}
                  className="group flex items-baseline gap-2.5 whitespace-nowrap px-6 py-1"
                >
                  <span className="font-display text-lg text-paper-200 transition-colors group-hover:text-paper-50">
                    {item.filmTitle}
                  </span>
                  {item.rating !== null && (
                    <span className="text-sm font-semibold tabular-nums text-lamp-400">
                      {formatRating(item.rating)}
                    </span>
                  )}
                </Link>

                <span aria-hidden="true" className="text-ink-500">
                  ✳
                </span>
              </li>
            ))}
          </ul>
        ))}
      </div>
    </div>
  );
}
